<template>
  <a-spin :spinning="loading">
    <div v-if="items.length" class="version-list">
      <article v-for="item in items" :key="item.id" class="version-card">
        <div class="version-meta">
          <div>
            <strong>{{ item.versionName || '-' }}</strong>
            <a-tag v-if="isOnline(item)" color="orange">
              当前线上
            </a-tag>
          </div>
          <span>{{ item.publishTime || '-' }}</span>
          <span>by {{ item.publishUser || '-' }}</span>
        </div>
        <div class="version-content">
          <h3>{{ item.releaseNote || '暂无版本说明' }}</h3>
          <p
            v-for="(change, changeIndex) in item.changes"
            :key="`${item.id}-${changeIndex}`"
          >
            – {{ change.description }}
            <span
              v-if="experiment && (change.variantName || change.variantKey)"
              class="change-variant"
            >
              （{{ change.variantName || '变体'
              }}{{ change.variantKey ? ` / ${change.variantKey}` : '' }}）
            </span>
          </p>
        </div>
      </article>
    </div>
    <a-empty
      v-else-if="!loading"
      :description="experiment ? '暂无实验记录' : '暂无版本记录'"
    />
    <a-alert v-if="loadError" type="error" :message="loadError">
      <template #action
        ><a-button size="small" @click="loadVersionHistory"
          >重试</a-button
        ></template
      >
    </a-alert>

    <div v-if="pagination.total" class="pagination-row">
      <span>共 {{ pagination.total }} 条</span>
      <a-pagination
        :current="pagination.pageNum"
        :page-size="pagination.pageSize"
        :total="pagination.total"
        :show-size-changer="true"
        :page-size-options="['10', '20', '50', '100']"
        @change="handlePageChange"
      />
    </div>
  </a-spin>
</template>

<script>
import { onBeforeUnmount, reactive, ref, watch } from 'vue'
import { message } from 'ant-design-vue'
import {
  appTab_config_publishedDetail_get,
  appTab_experiment_version_page_post,
  appTab_version_page_post
} from '@/server/modules/25824.js'
import { isOnlineVersion } from '../configDraft'

export default {
  props: {
    active: Boolean,
    experiment: Boolean,
    siteId: {
      type: [Number, String],
      default: undefined
    }
  },
  setup(props) {
    const items = ref([])
    const loading = ref(false)
    const loadError = ref('')
    const publishedDetail = ref(null)
    let requestId = 0
    const isOnline = item =>
      isOnlineVersion(item, publishedDetail.value, props.experiment)
    const pagination = reactive({
      pageNum: 1,
      pageSize: 20,
      pages: 0,
      total: 0
    })

    const loadVersionHistory = async () => {
      if (!props.siteId || !props.active) return
      const id = ++requestId
      const siteId = props.siteId
      const valid = () =>
        id === requestId && props.siteId === siteId && props.active
      loading.value = true
      loadError.value = ''
      publishedDetail.value = null
      try {
        const request = props.experiment
          ? appTab_experiment_version_page_post
          : appTab_version_page_post
        const catchError = error => ({
          success: false,
          retInfo: error?.message
        })
        const [page, detail] = await Promise.all([
          request({
            siteId: Number(siteId),
            pageNum: pagination.pageNum,
            pageSize: pagination.pageSize
          }).catch(catchError),
          appTab_config_publishedDetail_get({ siteId }).catch(catchError)
        ])
        if (!valid()) return
        const { success, data, retInfo } = page
        if (!success) {
          throw new Error(retInfo || '记录获取失败')
        }
        if (detail.success) publishedDetail.value = detail.data
        else
          message.error(detail.retInfo || '线上配置获取失败，暂不展示线上标记')
        items.value = Array.isArray(data?.list) ? data.list : []
        Object.assign(pagination, {
          pageNum: Number(data?.pageNum) || 1,
          pageSize: Number(data?.pageSize) || 20,
          pages: Number(data?.pages) || 0,
          total: Number(data?.total) || 0
        })
      } catch (error) {
        if (valid()) loadError.value = error?.message || '记录获取失败'
      } finally {
        if (valid()) loading.value = false
      }
    }
    const handlePageChange = async (pageNum, pageSize) => {
      pagination.pageNum = pagination.pageSize === pageSize ? pageNum : 1
      pagination.pageSize = pageSize
      await loadVersionHistory()
    }
    watch(
      () => [props.siteId, props.active],
      ([siteId, active], previous = []) => {
        requestId++
        loading.value = false
        loadError.value = ''
        publishedDetail.value = null
        if (siteId !== previous[0]) {
          items.value = []
          Object.assign(pagination, {
            pageNum: 1,
            pageSize: 20,
            pages: 0,
            total: 0
          })
        }
        if (siteId && active) loadVersionHistory()
      },
      { immediate: true, flush: 'sync' }
    )
    onBeforeUnmount(() => requestId++)

    return {
      handlePageChange,
      isOnline,
      items,
      loadError,
      loadVersionHistory,
      loading,
      pagination
    }
  }
}
</script>

<style lang="scss" scoped>
.version-list {
  display: grid;
  gap: 16px;
}

.version-card {
  display: grid;
  grid-template-columns: 220px 1fr;
  gap: 28px;
  padding: 22px;
  border: 1px solid #ffd8bf;
  border-radius: 6px;
  background: #fff;
}

.version-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: #8c8c8c;
  font-size: 12px;

  > div {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 2px;
  }

  strong {
    color: #262626;
    font-size: 18px;
  }
}

.version-content {
  min-width: 0;
  overflow-wrap: anywhere;
  h3 {
    margin: 0 0 8px;
    color: #595959;
    font-size: 14px;
    font-weight: 500;
  }

  p {
    margin: 5px 0;
    color: #595959;
  }
}

.change-variant {
  color: #8c8c8c;
}

.pagination-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 16px;
  color: #8c8c8c;
}
</style>
